export const money = (value: number): string =>
  "R$" + Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 2 });

// Weekly-tail breakdown: "17× R$50" on a round tail, "15× R$50 + 1× R$25"
// when the last installment carries a remainder (backend sends
// last_installment_value; absent/equal → the plain N× form).
export const installmentsLabel = (
  count: number,
  value: number,
  lastValue?: number,
): string => {
  const last = lastValue ?? value;
  if (count <= 0) return "";
  if (last === value) return `${count}× ${money(value)}`;
  if (count === 1) return `1× ${money(last)}`;
  return `${count - 1}× ${money(value)} + 1× ${money(last)}`;
};

const DOW = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const DOW_FULL = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
const MONTHS = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

export const parseISODate = (iso: string): Date => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};

export const shortDow = (iso: string): string => DOW[parseISODate(iso).getDay()];

export const dayMonth = (iso: string): string => {
  const d = parseISODate(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export const dateLabel = (iso: string): string => {
  const d = parseISODate(iso);
  return `${DOW_FULL[d.getDay()]}, ${d.getDate()} de ${MONTHS[d.getMonth()]}`;
};

// LEGACY fallback for a backend that predates `window_dates` (the bookable
// weekdays moved into the RTO_STATIONS flag on 2026-09-08, so the LP no longer
// knows the rule). All Tue–Sat days between two ISO dates, inclusive: the old
// window rule, kept only so an older backend still renders full days as
// "lotado" (they come absent from the station's days list).
export const tueSatBetween = (startIso: string, endIso: string): string[] => {
  const out: string[] = [];
  const end = parseISODate(endIso);
  for (let d = parseISODate(startIso); d <= end; d.setDate(d.getDate() + 1)) {
    const dow = d.getDay();
    if (dow >= 2 && dow <= 6) {
      out.push(
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
          d.getDate(),
        ).padStart(2, "0")}`,
      );
    }
  }
  return out;
};

// Which weekdays a set of bookable days covers, in rider words: "terça a
// sexta" when they run without gaps, "terça, quinta e sábado" when they don't.
// Derived from the dates the backend sent, never from a rule hardcoded here —
// that rule now lives in the RTO_STATIONS flag and can change any day.
export const weekdayWindowLabel = (dates: string[]): string => {
  const dows = [...new Set(dates.map((iso) => parseISODate(iso).getDay()))].sort((a, b) => a - b);
  if (dows.length === 0) return "";
  const names = dows.map((dow) => DOW_FULL[dow]);
  if (names.length === 1) return names[0];
  const contiguous = dows[dows.length - 1] - dows[0] === dows.length - 1;
  if (contiguous) return `${names[0]} a ${names[names.length - 1]}`;
  return `${names.slice(0, -1).join(", ")} e ${names[names.length - 1]}`;
};
